import React, { useEffect } from "react";
import { createStyles, makeStyles } from "@material-ui/core";
import { RegisterOptions, useController, useFormContext } from "react-hook-form";
import DocumentPicturePreview, { DocumentPicturePreviewHandle } from "./DocumentPicturePreview";
import DocumentFileButton from "./DocumentFileButton";
import DocumentImageViewer from "./DocumentImageViewer";
import useShowImage from "../../../../hooks/useShowImage";

type FormDocumentPreviewFormProps = {
  name: string;
  label: string;
  rules?: RegisterOptions;
  previewHeight?: number | string;
  compact?: boolean;
  fileName?: string;
  required?: boolean;
  displayVariant?: "camera" | "file";
  readOnly?: boolean;
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    label: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#364153",
      marginBottom: "4px",
    },
    requiredAsterisk: {
      color: "#EF4444",
    },
    error: {
      color: "#d32f2f",
      fontSize: "0.75rem",
      marginTop: "4px",
    },
  })
);

const FormDocumentPreviewForm: React.FC<FormDocumentPreviewFormProps> = ({
  name,
  label,
  rules,
  previewHeight,
  compact = false,
  required = false,
  displayVariant = "camera",
  readOnly = false,
  fileName,
}) => {
  const classes = useStyles();
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
    rules,
  });

  const getImage = useShowImage();
  const [imageUrl, setImageUrl] = React.useState("");
  const [isViewerOpen, setIsViewerOpen] = React.useState(false);
  const previewRef = React.useRef<DocumentPicturePreviewHandle>(null);
  const [shouldOpenCamera, setShouldOpenCamera] = React.useState(false);

  React.useEffect(() => {
    if (shouldOpenCamera && !imageUrl) {
      previewRef.current?.openCamera();
      setShouldOpenCamera(false);
    }
  }, [shouldOpenCamera, imageUrl]);

  useEffect(() => {
    if (field.value instanceof File) {
      const fileUrl = URL.createObjectURL(field.value);
      setImageUrl(fileUrl);
      return () => URL.revokeObjectURL(fileUrl);
    } else if (typeof field.value === "string" && field.value) {
      setImageUrl(getImage(field.value));
      return undefined;
    }

    setImageUrl("");
    return undefined;
  }, [field.value, getImage]);

  const resolvedPreviewHeight = compact && previewHeight === undefined ? 50 : previewHeight;

  const renderLabel = (labelText: string) => {
    const labelTextWithAsterisk =
      required && !labelText.includes("*") ? `${labelText} *` : labelText;
    const requiredMatch = labelTextWithAsterisk.match(/^(.*?)(\s*\*)$/);
    if (!requiredMatch) {
      return labelTextWithAsterisk;
    }

    return (
      <>
        {requiredMatch[1]}
        <span className={classes.requiredAsterisk}>{requiredMatch[2]}</span>
      </>
    );
  };

  return (
    <div data-field-name={name} className={classes.root}>
      <span className={classes.label}>{renderLabel(label)}</span>
      {displayVariant === "file" ? (
        <>
          {imageUrl ? (
            <>
              <DocumentFileButton
                label="Ver archivo"
                height={resolvedPreviewHeight ?? 96}
                onClick={() => setIsViewerOpen(true)}
              />
              <DocumentImageViewer
                open={isViewerOpen}
                onClose={() => setIsViewerOpen(false)}
                imageUrl={imageUrl}
                alt={label}
                fileName={fileName || (field.value instanceof File ? field.value.name : label)}
                allowDelete={!readOnly}
                onDelete={() => {
                  field.onChange(null);
                }}
                onRetake={
                  !readOnly
                    ? () => {
                        setIsViewerOpen(false);
                        field.onChange(null);
                        setShouldOpenCamera(true);
                      }
                    : undefined
                }
              />
            </>
          ) : (
            <DocumentPicturePreview
              ref={previewRef}
              label={label}
              imageUrl={imageUrl}
              previewHeight={resolvedPreviewHeight}
              placeholderHeight={resolvedPreviewHeight}
              labelSize={compact ? "compact" : "normal"}
              onUpload={(file) => field.onChange(file || null)}
              readOnly={readOnly}
              allowClear={!readOnly}
              allowImageViewer={true}
            />
          )}
        </>
      ) : (
        <DocumentPicturePreview
          label={label}
          imageUrl={imageUrl}
          previewHeight={resolvedPreviewHeight}
          placeholderHeight={resolvedPreviewHeight}
          labelSize={compact ? "compact" : "normal"}
          onUpload={(file) => field.onChange(file || null)}
          readOnly={readOnly}
          allowClear={!readOnly}
          allowImageViewer={true}
        />
      )}
      {fieldState.error?.message ? (
        <span className={classes.error}>{fieldState.error.message}</span>
      ) : null}
    </div>
  );
};

export default FormDocumentPreviewForm;
